import { useQuery } from "@apollo/client";
import { GET_INVOICE_BY_ID, GetInvoiceByIdData, GetInvoiceByIdVars } from "common/graphql";
import React from "react";
import LocalLevelError from "../common/LocalLevelError";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import LoadingSkeleton from "../LoadingSkeleton";
import _times from "lodash/times";
import { InvoiceParkEventCard } from "./InvoiceParkEventCard";

export interface InvoiceParkEventsProps {
  invoiceId: string;
  onClickParkEvent: (transactionId: string) => void;
}

export const InvoiceParkEvents: React.FC<InvoiceParkEventsProps> = ({ invoiceId, onClickParkEvent }) => {
  const {
    data: invoiceData,
    loading: invoiceLoading,
    refetch: refetchInvoice,
    error: invoiceError,
  } = useQuery<GetInvoiceByIdData, GetInvoiceByIdVars>(GET_INVOICE_BY_ID, {
    variables: { invoiceId },
  });

  const refetchOnError = () => {
    if (invoiceError) {
      refetchInvoice();
    }
  };

  const invoice = invoiceData?.invoice;

  if (invoiceError) {
    return <LocalLevelError onTryAgain={refetchOnError} />;
  }

  return (
    <Paper sx={{ pt: 3, pb: 3, boxShadow: 3, borderRadius: 2 }}>
      <Box mb={3} ml={3} mr={3} display="flex" alignItems="center" justifyContent="space-between">
        <LoadingSkeleton loading={invoiceLoading} height={60}>
          <Typography variant="h5" fontWeight={400}>
            Event Details
          </Typography>
        </LoadingSkeleton>
        {!invoiceLoading && (invoice?.transactions?.length ?? 0) > 1 && (
          <Chip label={`${invoice?.transactions?.length} events`} size="small" color="info" />
        )}
      </Box>
      {/* Loading skeletons — one per expected card */}
      {invoiceLoading && (
        <Box sx={{ px: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          {_times(3, (index) => (
            <Skeleton key={index} variant="rectangular" height={500} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      )}

      {/* Event cards */}
      {!invoiceLoading && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4, px: 3 }}>
          {!invoice || !invoice.transactions?.length ? (
            <Typography variant="h6" m={1.5}>
              There are currently no transactions to report
            </Typography>
          ) : (
            invoice.transactions.map((transaction, index) => (
              <Box key={transaction.id}>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  Event {index + 1}
                </Typography>
                <InvoiceParkEventCard
                  transaction={transaction}
                  registeredVehicle={invoice.registered_vehicle}
                  onClickViewDetails={() => onClickParkEvent(transaction.id)}
                />
              </Box>
            ))
          )}
        </Box>
      )}
    </Paper>
  );
};

export default InvoiceParkEvents;
